import type { JSX } from "react"
import {
  Zap,
  Coffee,
  Cog,
  Boxes,
  Shirt,
  Pill,
  Bot,
  Gift,
  LineChart,
  Sun,
  Wheat,
  Building2,
  FileText,
  Shield,
  Car,
  Sofa,
  Printer,
  Heart,
  Briefcase,
  Gem,
  Trophy,
  Leaf,
  FlaskConical
} from "lucide-react"
import { IconPepper } from "@tabler/icons-react"

const ICON_CLASS = "size-6 transition-transform group-hover:scale-105"
const R = "#e6202f"

function SpiceIcon() {
  return (
    <svg className={ICON_CLASS} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5c-1 1-1.5 2.5-1 4" />
      <path
        d="M9 5c2-1.5 4.5-1 5.5 1.5 1.5 3.5-.5 9-4.5 11.5-3 1.8-6.3.2-6.8-3-.5-3.3 1.3-6.7 4-8.7.6-.4.9-1.1.6-1.8-.2-.6-.7-1-1.3-1"
        fill={R}
        fillOpacity="0.12"
      />
    </svg>
  )
}

function DefaultCategoryIcon() {
  return (
    <svg className={ICON_CLASS} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" strokeWidth="3" stroke={R} />
    </svg>
  )
}

export const CATEGORY_ICON_MAP: Record<string, () => JSX.Element> = {
  "Electronics & Electricals": () => <Zap className={ICON_CLASS} />,
  "Tea & Coffee": () => <Coffee className={ICON_CLASS} />,
  "Industrial Machinery": () => <Cog className={ICON_CLASS} />,
  "Metals & Alloys": () => <Boxes className={ICON_CLASS} />,
  "Building & Construction": () => <Building2 className={ICON_CLASS} />,
  "Textiles": () => <Shirt className={ICON_CLASS} />,
  "Pharmaceuticals": () => <Pill className={ICON_CLASS} />,
  "Industrial Automation": () => <Bot className={ICON_CLASS} />,
  "Gifts & Crafts": () => <Gift className={ICON_CLASS} />,
  "SPC": () => <LineChart className={ICON_CLASS} />,
  "Renewable Energy": () => <Sun className={ICON_CLASS} />,
  "Plastics & Polymers": () => <FlaskConical className={ICON_CLASS} />,
  "Food & Agriculture": () => <Wheat className={ICON_CLASS} />,
  "Spice": () => <IconPepper className={ICON_CLASS} stroke={1.75} />,
}

export function getCategoryIcon(categoryName: string): JSX.Element {
  const normalized = categoryName.trim().toLowerCase()
  
  // 1. Agriculture & Food Products
  if (normalized.includes("agriculture") || normalized.includes("food") || normalized.includes("grain") || normalized.includes("fruit") || normalized.includes("vegetable") || normalized.includes("spice") || normalized.includes("seasoning") || normalized.includes("dairy") || normalized.includes("poultry") || normalized.includes("seafood") || normalized.includes("honey")) {
    if (normalized.includes("spice") || normalized.includes("seasoning") || normalized.includes("pepper")) {
      return <IconPepper className={ICON_CLASS} stroke={1.75} />
    }
    return <Wheat className={ICON_CLASS} />
  }
  
  // 2. Apparel & Fashion
  if (normalized.includes("apparel") || normalized.includes("fashion") || normalized.includes("textile") || normalized.includes("clothing") || normalized.includes("garment") || normalized.includes("fabric") || normalized.includes("yarn") || normalized.includes("footwear")) {
    return <Shirt className={ICON_CLASS} />
  }
  
  // 3. Machinery
  if (normalized.includes("machinery") || normalized.includes("equipment") || normalized.includes("pump") || normalized.includes("valve") || normalized.includes("cnc")) {
    return <Cog className={ICON_CLASS} />
  }
  
  // 4. Electronics & Electricals
  if (normalized.includes("electronics") || normalized.includes("electrical") || normalized.includes("wire") || normalized.includes("cable") || normalized.includes("appliance") || normalized.includes("led") || normalized.includes("semiconductor") || normalized.includes("telecom")) {
    return <Zap className={ICON_CLASS} />
  }
  
  // 5. Chemicals & Pharmaceuticals
  if (normalized.includes("chemical") || normalized.includes("pharmaceutical") || normalized.includes("drug") || normalized.includes("fertilizer") || normalized.includes("pesticide") || normalized.includes("cosmetic raw") || normalized.includes("petrochemical")) {
    return <Pill className={ICON_CLASS} />
  }
  
  // 6. Construction & Building Materials
  if (normalized.includes("construction") || normalized.includes("building") || normalized.includes("cement") || normalized.includes("concrete") || normalized.includes("tile") || normalized.includes("marble") || normalized.includes("granite") || normalized.includes("sanitaryware") || normalized.includes("pipes") || normalized.includes("roofing") || normalized.includes("paint")) {
    return <Building2 className={ICON_CLASS} />
  }
  
  // 7. Metals & Minerals
  if (normalized.includes("metal") || normalized.includes("mineral") || normalized.includes("iron") || normalized.includes("steel") || normalized.includes("aluminum") || normalized.includes("copper") || normalized.includes("zinc") || normalized.includes("ore") || normalized.includes("scrap")) {
    return <Boxes className={ICON_CLASS} />
  }
  
  // 8. Automotive & Transportation
  if (normalized.includes("automotive") || normalized.includes("transport") || normalized.includes("vehicle") || normalized.includes("tire") || normalized.includes("lubricant") || normalized.includes("car")) {
    return <Car className={ICON_CLASS} />
  }
  
  // 9. Furniture & Home Décor
  if (normalized.includes("furniture") || normalized.includes("home decor") || normalized.includes("kitchenware") || normalized.includes("tableware") || normalized.includes("furnishings") || normalized.includes("sofa")) {
    return <Sofa className={ICON_CLASS} />
  }
  
  // 10. Packaging & Printing
  if (normalized.includes("packaging") || normalized.includes("printing") || normalized.includes("labeling") || normalized.includes("box") || normalized.includes("container") || normalized.includes("paperboard")) {
    return <Printer className={ICON_CLASS} />
  }
  
  // 11. Health, Beauty & Personal Care
  if (normalized.includes("health") || normalized.includes("beauty") || normalized.includes("personal care") || normalized.includes("skincare") || normalized.includes("cosmetics") || normalized.includes("haircare") || normalized.includes("hygiene") || normalized.includes("wellness") || normalized.includes("ayurvedic")) {
    return <Heart className={ICON_CLASS} />
  }
  
  // 12. Leather & Leather Goods
  if (normalized.includes("leather") || normalized.includes("wallet") || normalized.includes("bags") || normalized.includes("bag")) {
    return <Briefcase className={ICON_CLASS} />
  }
  
  // 13. Jewelry, Gems & Watches
  if (normalized.includes("jewelry") || normalized.includes("gems") || normalized.includes("gem") || normalized.includes("watch") || normalized.includes("gold") || normalized.includes("silver") || normalized.includes("precious stone")) {
    return <Gem className={ICON_CLASS} />
  }
  
  // 14. Sports, Toys & Leisure
  if (normalized.includes("sports") || normalized.includes("toys") || normalized.includes("toy") || normalized.includes("leisure") || normalized.includes("fitness") || normalized.includes("camping")) {
    return <Trophy className={ICON_CLASS} />
  }
  
  // 15. Energy & Environment
  if (normalized.includes("energy") || normalized.includes("environment") || normalized.includes("solar") || normalized.includes("renewable") || normalized.includes("waste") || normalized.includes("water treatment")) {
    return <Leaf className={ICON_CLASS} />
  }
  
  // 16. Office & Stationery Supplies
  if (normalized.includes("office") || normalized.includes("stationery") || normalized.includes("paper product") || normalized.includes("writing instrument") || normalized.includes("school supply")) {
    return <FileText className={ICON_CLASS} />
  }
  
  // 17. Security & Safety Equipment
  if (normalized.includes("security") || normalized.includes("safety") || normalized.includes("ppe") || normalized.includes("fire safety") || normalized.includes("surveillance") || normalized.includes("lock") || normalized.includes("access control")) {
    return <Shield className={ICON_CLASS} />
  }
  
  // 18. Gifts, Crafts & Handmade Products
  if (normalized.includes("gift") || normalized.includes("craft") || normalized.includes("handmade") || normalized.includes("handicraft") || normalized.includes("religious") || normalized.includes("spiritual")) {
    return <Gift className={ICON_CLASS} />
  }
  
  return <DefaultCategoryIcon />
}
